import * as React from 'react';

// Standard Tailwind breakpoints
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

/** Viewports below this width use mobile layouts (matches Tailwind `lg`). */
export const MOBILE_MAX_WIDTH = BREAKPOINTS.lg;

export function getIsMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < MOBILE_MAX_WIDTH;
}

/** Subscribe to viewport changes including iOS orientation (delayed innerWidth update). */
function subscribeViewport(onChange: () => void) {
  if (typeof window === 'undefined') return () => {};

  const notify = () => onChange();
  let orientationTimer: ReturnType<typeof setTimeout> | undefined;

  const onOrientationChange = () => {
    if (orientationTimer) clearTimeout(orientationTimer);
    notify();
    orientationTimer = setTimeout(notify, 350);
  };

  window.addEventListener('resize', notify);
  window.addEventListener('orientationchange', onOrientationChange);
  window.visualViewport?.addEventListener('resize', notify);

  return () => {
    if (orientationTimer) clearTimeout(orientationTimer);
    window.removeEventListener('resize', notify);
    window.removeEventListener('orientationchange', onOrientationChange);
    window.visualViewport?.removeEventListener('resize', notify);
  };
}

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(getIsMobile);

  React.useEffect(() => {
    const update = () => setIsMobile(getIsMobile());
    update();
    const mql = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH - 1}px)`);
    mql.addEventListener('change', update);
    const unsubscribe = subscribeViewport(update);
    return () => {
      mql.removeEventListener('change', update);
      unsubscribe();
    };
  }, []);

  return isMobile;
}

export function useIsTablet() {
  const [isTablet, setIsTablet] = React.useState(() => {
    if (typeof window === 'undefined') return false;
    return (
      window.innerWidth >= BREAKPOINTS.md &&
      window.innerWidth < MOBILE_MAX_WIDTH
    );
  });

  React.useEffect(() => {
    const update = () => {
      setIsTablet(
        window.innerWidth >= BREAKPOINTS.md &&
          window.innerWidth < MOBILE_MAX_WIDTH
      );
    };
    const mql = window.matchMedia(
      `(min-width: ${BREAKPOINTS.md}px) and (max-width: ${MOBILE_MAX_WIDTH - 1}px)`
    );
    mql.addEventListener('change', update);
    update();
    const unsubscribe = subscribeViewport(update);
    return () => {
      mql.removeEventListener('change', update);
      unsubscribe();
    };
  }, []);

  return isTablet;
}

export function useIsDesktop() {
  const [isDesktop, setIsDesktop] = React.useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= MOBILE_MAX_WIDTH;
  });

  React.useEffect(() => {
    const update = () => {
      setIsDesktop(window.innerWidth >= MOBILE_MAX_WIDTH);
    };
    const mql = window.matchMedia(`(min-width: ${MOBILE_MAX_WIDTH}px)`);
    mql.addEventListener('change', update);
    update();
    const unsubscribe = subscribeViewport(update);
    return () => {
      mql.removeEventListener('change', update);
      unsubscribe();
    };
  }, []);

  return isDesktop;
}

export function useBreakpoint() {
  const [breakpoint, setBreakpoint] = React.useState<
    'sm' | 'md' | 'lg' | 'xl' | '2xl'
  >('lg');

  React.useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width < BREAKPOINTS.sm) setBreakpoint('sm');
      else if (width < BREAKPOINTS.md) setBreakpoint('md');
      else if (width < BREAKPOINTS.lg) setBreakpoint('lg');
      else if (width < BREAKPOINTS.xl) setBreakpoint('xl');
      else setBreakpoint('2xl');
    };

    updateBreakpoint();
    const unsubscribe = subscribeViewport(updateBreakpoint);
    return unsubscribe;
  }, []);

  return breakpoint;
}
